import axios, { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'
import { API_BASE_URL, X_API_KEY } from '../services/api'

interface Announce {
  id: number
  practices: number[]
}

const fetchAnnouncess = async (): Promise<Announce[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/all/full`, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })

    if (!response.ok) {
      throw new Error('Failed to fetch data')
    }

    const data = await response.json()

    let announces: Announce[] = []

    if (data && data.practiceList) {
      announces = data.practiceList
        .flatMap((category: any) => category.practices)
        .map((practice: any) => ({
          id: practice.id,
          practices: [practice.id],
        }))
    } else if (data && data.users) {
      announces = data.users
        .filter((user: any) => user.announce !== null)
        .map((user: any) => user.announce)
    }

    return announces
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchAnnouncess',
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchAnnouncess',
      },
    })
    throw error
  }
}

const fetchGalleryByUserId = async (userId: number): Promise<string> => {
  try {
    const response = await fetch(`${API_BASE_URL}/${userId}/gallery`, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch gallery')
    }
    const galleryData = await response.json()
    return `path/to/your/images/${galleryData.fileName}`
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchGalleryByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchGalleryByUserId',
      },
    })
    throw error
  }
}

const fetchTimings = async (): Promise<string[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/announces`, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch announces')
    }
    const announcesData = await response.json()
    const timings = announcesData.flatMap((announce: any) => announce.timing)
    return Array.from(new Set(timings))
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchTimings',
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchTimings',
      },
    })
    throw error
  }
}

const postMyAnnounce = async (
  myAnnounce: any,
  token: string,
  userId: string,
): Promise<void> => {
  const requestBody = {
    userId: Number(userId), // ✅ S'assurer que c'est une string
    title: myAnnounce.title, // ✅ Obligatoire
    description: myAnnounce.description, // ✅ Obligatoire
    email: myAnnounce.email || null, // ✅ Obligatoire
    website: myAnnounce.website || null, // ✅ Valeur par défaut
    infidelityCard: myAnnounce.infidelityCard || null, // ✅ Valeur par défaut
    status: myAnnounce.status || ['private, escort, massage'], // ✅ Valeur par défaut
    practices: myAnnounce.practices || [], // ✅ Valeur par défaut
    timing: myAnnounce.timing || [], // ✅ Valeur par défaut
    price: myAnnounce.price || [], // ✅ Valeur par défaut
    timingEscort: myAnnounce.timingEscort || [], // ✅ Valeur par défaut
    priceEscort: myAnnounce.priceEscort || [], // ✅ Valeur par défaut
    disponibility: myAnnounce.disponibility || [], // ✅ Valeur par défaut
    availabilityStart: myAnnounce.availabilityStart || [], // ✅ Valeur par défaut
    availabilityEnd: myAnnounce.availabilityEnd || [], // ✅ Valeur par défaut
    views: myAnnounce.views || 0, // ✅ Ajouté pour éviter les erreurs
    escort: myAnnounce.escort || false, // ✅ Valeur par défaut
    isAvailable: myAnnounce.isAvailable || false, // ✅ Valeur par défaut
    location: Array.isArray(myAnnounce.location) ? myAnnounce.location : [],
    whatsapp: myAnnounce.whatsapp || false, // ✅ Valeur par défaut
    smsOnly: myAnnounce.smsOnly || false, // ✅ Valeur par défaut
    callOnly: myAnnounce.callOnly || false, // ✅ Valeur par défaut
    emailOnly: myAnnounce.emailOnly || false, // ✅ Valeur par défaut
    boosted: myAnnounce.boosted || false, // ✅ Valeur par défaut
    boostedTime:
      myAnnounce.boostedTime === 'null' ? null : myAnnounce.boostedTime,
  }

  console.log("📤 Envoi de l'annonce...")
  console.log('🌍 URL:', `${API_BASE_URL}/user/${userId}/announce/`)
  console.log('🔑 Headers:', {
    Authorization: `Bearer ${token}`,
    'x-api-key': X_API_KEY,
    'Content-Type': 'application/json',
  })
  console.log('📦 Body envoyé:', JSON.stringify(requestBody, null, 2))

  try {
    const response = await fetch(`${API_BASE_URL}/user/${userId}/announce/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error("❌ Réponse détaillée de l'API :", errorText)
      throw new Error(`❌ API ERROR ${response.status}: ${errorText}`)
    }

    console.log('✅ Annonce envoyée avec succès !')
  } catch (error) {
    console.error("❌ Erreur lors de l'envoi :", error)

    Sentry.captureException(error, {
      extra: {
        action: 'postMyAnnounce',
        userId,
        requestBody,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'postMyAnnounce',
      },
    })

    throw error
  }
}

const fetchAnnounceByUserId = async (userId: string, token: string) => {
  const url = `${API_BASE_URL}/user/${userId}/announce`

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    if (response.data && response.data.announce) {
      return response.data.announce
    }

    if (response.data && (response.data.id || response.data.userId)) {
      return response.data
    }

    return null
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400 || error.response?.status === 404) {
        return null
      }

      if (error.response?.status === 401) {
        throw new Error('unauthorized')
      }

      if (error.response?.status === 500) {
        return null
      }

      Sentry.captureException(error, {
        extra: {
          action: 'fetchAnnounceByUserId',
          userId,
          statusCode: error.response?.status,
          errorData: error.response?.data,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'fetchAnnounceByUserId',
          statusCode: error.response?.status?.toString(),
        },
      })

      return null
    }

    Sentry.captureException(error)
    return null
  }
}

const deleteMyAnnounce = async (
  announceId: number | string,
  token: string,
): Promise<void> => {
  const DELETE_API_URL = `${API_BASE_URL}/user/${announceId}/announce`

  try {
    const response = await axios.delete(DELETE_API_URL, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': X_API_KEY,
      },
    })

    if (response.status === 200) {
      localStorage.removeItem('myAnnounce')
    } else {
      throw new Error('Failed to delete announce')
    }
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'deleteMyAnnounce',
        announceId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'deleteMyAnnounce',
      },
    })
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error
    } else {
      throw error
    }
  }
}

const updateLocalStorageWithUserAnnounce = (userAnnounce: any) => {
  if (
    !userAnnounce ||
    (Array.isArray(userAnnounce) && userAnnounce.length === 0)
  ) {
    localStorage.setItem('myAnnounce', JSON.stringify({}))
  } else {
    const updatedAnnounce = {
      ...userAnnounce,
      practices: userAnnounce.practices || [],
      disponibility: userAnnounce.disponibility || [],
      availabilityStart: userAnnounce.availabilityStart || [],
      availabilityEnd: userAnnounce.availabilityEnd || [],
    }

    localStorage.setItem('myAnnounce', JSON.stringify(updatedAnnounce))

    console.log('✅ LocalStorage mis à jour avec :', updatedAnnounce)
  }
}

const getUserAnnounces = async (
  userId: string,
  token: string,
): Promise<any> => {
  const USER_ANNOUNCE_API_URL = `${API_BASE_URL}/user/${userId}/announce`

  try {
    const response = await fetch(USER_ANNOUNCE_API_URL, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch user announce')
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'getUserAnnounces',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'getUserAnnounces',
      },
    })
    throw error
  }
}

const updateMyAnnounce = async (
  userId: string,
  token: string,
  dataToSend: any,
) => {
  const url = `${API_BASE_URL}/user/${userId}/announce`

  // 🔥 Récupérer les données du localStorage et les fusionner avec les nouvelles données
  const localStoredAnnounceStr = localStorage.getItem('myAnnounce')
  const localStoredAnnounce = localStoredAnnounceStr
    ? JSON.parse(localStoredAnnounceStr)
    : {}

  const finalData = {
    ...localStoredAnnounce, // Conserver les données locales
    ...dataToSend, // Appliquer les nouvelles modifications
    practices: Array.isArray(dataToSend.practices)
      ? dataToSend.practices
      : localStoredAnnounce.practices || [],
    disponibility: Array.isArray(dataToSend.disponibility)
      ? dataToSend.disponibility
      : localStoredAnnounce.disponibility || [],
    availabilityStart: Array.isArray(dataToSend.availabilityStart)
      ? dataToSend.availabilityStart
      : localStoredAnnounce.availabilityStart || [],
    availabilityEnd: Array.isArray(dataToSend.availabilityEnd)
      ? dataToSend.availabilityEnd
      : localStoredAnnounce.availabilityEnd || [],
    boostedTime:
      dataToSend.boostedTime || localStoredAnnounce.boostedTime || null,
  }

  console.log('📤 FINAL API CALL - requestBody:', finalData)

  try {
    const response = await axios.patch(url, finalData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': X_API_KEY,
      },
    })

    // 🎯 Mettre à jour le localStorage après mise à jour réussie
    localStorage.setItem('myAnnounce', JSON.stringify(finalData))

    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'updateMyAnnounce',
        userId,
        finalData,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'updateMyAnnounce',
      },
    })

    if (axios.isAxiosError(error)) {
      throw error.response?.data || error
    } else {
      throw error
    }
  }
}

const fetchAnnounceDetails = async (id: string): Promise<any> => {
  if (!id || id === 'default') {
    console.warn(
      "⚠️ Tentative de récupération d'une annonce avec un ID invalide :",
      id,
    )
    return null
  }
  const ANNOUNCE_API_URL = `${API_BASE_URL}/announces/${id}`

  try {
    const response = await fetch(ANNOUNCE_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch announce with id ${id}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchAnnounceDetails',
        id,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchAnnounceDetails',
      },
    })
    throw error
  }
}

const fetchAnnouncesDetailsByUserId = async (userId: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/announce?userId=${userId}`, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch Announces details')
    }
    return response.json()
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchAnnouncesDetailsByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchAnnouncesDetailsByUserId',
      },
    })
    throw error
  }
}

const updateAnnounceUpdatedAt = async (
  userId: string,
  token: string,
  announce: any,
) => {
  const url = `${API_BASE_URL}/user/${userId}/announce`

  try {
    const response = await axios.patch(
      url,
      { ...announce },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-api-key': X_API_KEY,
        },
      },
    )
    return response.data.announce
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'updateAnnounceUpdatedAt',
        userId,
        announce,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'updateAnnounceUpdatedAt',
      },
    })
    if (axios.isAxiosError(error)) {
      throw (
        (error as AxiosError).response?.data || (error as AxiosError).message
      )
    } else {
      throw error
    }
  }
}

const incrementAnnounceViews = async (
  userId: string,
  token: string,
  announce: any,
) => {
  const url = `${API_BASE_URL}/user/${userId}/announce`

  const updatedAnnounce = {
    ...announce,
    views: announce.views + 1,
  }

  try {
    const response = await axios.patch(url, updatedAnnounce, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': X_API_KEY,
      },
    })
    return response.data.announce
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'incrementAnnounceViews',
        userId,
        announce,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'incrementAnnounceViews',
      },
    })
    if (axios.isAxiosError(error)) {
      throw (
        (error as AxiosError).response?.data || (error as AxiosError).message
      )
    } else {
      throw error
    }
  }
}

export {
  incrementAnnounceViews,
  updateAnnounceUpdatedAt,
  fetchAnnouncess,
  updateMyAnnounce,
  deleteMyAnnounce,
  fetchAnnounceByUserId,
  fetchAnnouncesDetailsByUserId,
  fetchGalleryByUserId,
  fetchAnnounceDetails,
  fetchTimings,
  postMyAnnounce,
  updateLocalStorageWithUserAnnounce,
  getUserAnnounces,
}
